/**
 * ToastContainer.tsx - Customer app toast notification renderer
 */

import React, { useState, useEffect } from 'react';
import toastService, { Toast } from '../../services/toastService';

const ICONS: Record<Toast['type'], string> = {
  success: '✓',
  error: '✕',
  warning: '⚠',
  info: 'ℹ',
};

const COLORS: Record<Toast['type'], string> = {
  success: '#22c55e',
  error: '#ef4444',
  warning: '#f59e0b',
  info: '#3b82f6',
};

const ToastContainer: React.FC = () => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  useEffect(() => {
    const unsub = toastService.subscribe((toast) => {
      setToasts((prev) => [...prev, toast]);
    });
    const unsubRemove = toastService.subscribeRemove((id) => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    });
    return () => {
      unsub();
      unsubRemove();
    };
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div style={{
      position: 'fixed',
      bottom: 'calc(64px + env(safe-area-inset-bottom, 0px) + 12px)',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      pointerEvents: 'none',
      width: 'min(90vw, 360px)',
    }}>
      {toasts.map((toast) => (
        <div
          key={toast.id}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '12px 16px',
            borderRadius: '12px',
            background: 'rgba(30, 30, 40, 0.95)',
            backdropFilter: 'blur(8px)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
            color: '#fff',
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: 1.4,
            pointerEvents: 'auto',
            animation: 'slideUp 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          <span style={{
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            background: COLORS[toast.type],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            flexShrink: 0,
          }}>
            {ICONS[toast.type]}
          </span>
          <span style={{ flex: 1 }}>{toast.message}</span>
          {toast.action && (
            <button
              type="button"
              onClick={() => {
                toast.action?.onClick();
                toastService.remove(toast.id);
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 700,
                minHeight: '44px',
                minWidth: '44px',
                padding: '8px 12px',
                flexShrink: 0,
                textDecoration: 'underline',
              }}
            >
              {toast.action.label}
            </button>
          )}
          <button
            onClick={() => toastService.remove(toast.id)}
            style={{
              background: 'none',
              border: 'none',
              color: 'rgba(255,255,255,0.5)',
              cursor: 'pointer',
              fontSize: '16px',
              padding: '2px',
              lineHeight: 1,
              flexShrink: 0,
            }}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      ))}
      <style>{`@keyframes slideUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </div>
  );
};

export default ToastContainer;
